import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { api, fmt } from "@/lib/api";
import { VerdictBadge, UnattestedBadge } from "@/components/CertificationBadges";
import PeriodCloseChecklistTile from "@/components/PeriodCloseChecklistTile";

const STATUS_TAG = {
  not_started: { label: "Awaiting Import", cls: "tag attn" },
  in_progress: { label: "Needs Review", cls: "tag attn" },
  complete: { label: "Complete", cls: "tag ok" },
  locked: { label: "Locked", cls: "tag ok" },
};

export default function Hub() {
  const [data, setData] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.hub(), api.getSettings().catch(() => null)])
      .then(([d, s]) => {
        setData(d);
        if (s?.logo_asset_id) setCompanyLogo(api.assetUrl(s.logo_asset_id));
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  if (loading) return <div className="loading" data-testid="hub-loading">Loading…</div>;
  if (!data) return <div className="loading">Failed to load hub.</div>;

  const { kpis, worklist } = data;
  const active = worklist.filter(w => w.status !== "locked");
  // Locked periods — sort reverse-chronological per Controller direction
  const completed = worklist
    .filter(w => w.status === "locked")
    .sort((a, b) =>
      (b.period_year - a.period_year) || (b.period_month - a.period_month),
    );

  return (
    <div data-testid="hub-page">
      <header style={{
        marginBottom: 24, display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", gap: 24,
      }}>
        <div>
          <div className="heading h1" data-testid="hub-title">Month-End Hub</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>
            Pick the period that needs you most. Or upload a statement to start a new one.
          </div>
        </div>
        <img
          src={companyLogo || ""}
          alt="Company logo"
          data-testid="hub-company-logo"
          style={{
            maxWidth: 200, maxHeight: 90, objectFit: "contain",
            flexShrink: 0,
            display: companyLogo ? "block" : "none",
          }}
        />
      </header>

      <div className="kpi-strip" data-testid="hub-kpis">
        <Kpi label="Periods Tracked" value={kpis.total_periods} testid="kpi-total" />
        <Kpi label="Closed" value={kpis.closed_periods} cls="accent" testid="kpi-closed" />
        <Kpi label="Blocked" value={kpis.blocked_periods} cls={kpis.blocked_periods > 0 ? "amber" : ""} testid="kpi-blocked" />
        <Kpi label="Unexplained Variance"
             value={"$" + fmt.currency(kpis.unexplained_variance_total)}
             cls={kpis.unexplained_variance_total > 0 ? "amber" : ""}
             testid="kpi-unexplained" />
      </div>

      <PeriodCloseChecklistTile />

      <section className="worklist-section" data-testid="worklist-active">
        <div className="worklist-title">
          <div className="heading h2">Worklist</div>
          <span className="muted" style={{ fontSize: 12 }}>Ranked by what needs you next</span>
        </div>
        {active.length === 0 && <div className="empty">No active periods. You&apos;re all caught up.</div>}
        {active.map(w => <ActiveRow key={w.period_id} item={w} />)}
      </section>

      {completed.length > 0 && (
        <section className="worklist-section" style={{ marginTop: 36 }} data-testid="worklist-closed">
          <div className="worklist-title">
            <div className="heading h3 muted">Closed Periods</div>
          </div>
          <div
            data-testid="closed-records-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {completed.slice(0, 12).map(w => <RecordCard key={w.period_id} item={w} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value, cls = "", testid }) {
  return (
    <div className="kpi" data-testid={testid}>
      <div className="kpi-label">{label}</div>
      <div className={"kpi-value " + cls}>{value}</div>
    </div>
  );
}

// ─── Active (work) row — unchanged behavior; minor: drift badge removed ─────
function ActiveRow({ item }) {
  const status = STATUS_TAG[item.status] || { label: item.status, cls: "tag idle" };
  const variance = Math.max(Math.abs(item.bank_variance || 0), Math.abs(item.gl_variance || 0));
  const needsImport = !item.has_imports && item.status !== "locked";
  const blockers = (item.under_review_count || 0) + (item.pending_duplicate_count || 0);
  const recurringOutstanding = item.recurring_outstanding_count || 0;

  let actionLabel = "Open Workspace";
  if (needsImport) actionLabel = "Upload Statement";
  else if (variance > 0 && !item.variance_explained) actionLabel = "Investigate";

  return (
    <div className="worklist-row" data-testid={`worklist-${item.period_id}`}>
      <div className="primary">
        <div>
          <span className="account">{item.account_name}</span>
          <span className="period-label"> · {item.period_label}</span>
        </div>
        <div className="meta">
          {blockers > 0 && (
            <>
              {item.under_review_count > 0 && <>{item.under_review_count} under review · </>}
              {item.pending_duplicate_count > 0 && <>{item.pending_duplicate_count} duplicate cluster · </>}
            </>
          )}
          {item.has_imports
            ? <>Bank variance <span className="mono">{fmt.currency(item.bank_variance)}</span> · GL variance <span className="mono">{fmt.currency(item.gl_variance)}</span></>
            : <>No statement uploaded yet</>}
          {recurringOutstanding > 0 && (
            <>
              {" · "}
              <Link
                to="/recurring-journals"
                className="rj-badge"
                title={`${recurringOutstanding} recurring journal${recurringOutstanding === 1 ? "" : "s"} outstanding for ${item.recurring_month_key} — informational only`}
                data-testid={`rj-badge-${item.period_id}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "1px 8px", fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.02em", borderRadius: 999,
                  border: "1px solid var(--line)",
                  background: "var(--surface-elev)", color: "var(--ink)",
                  textDecoration: "none",
                }}
              >
                <span style={{ fontFamily: "var(--serif)", fontSize: 12 }}>{recurringOutstanding}</span>
                <span style={{ color: "var(--ink-mute)", textTransform: "uppercase", fontSize: 10 }}>
                  recurring outstanding
                </span>
              </Link>
            </>
          )}
        </div>
      </div>
      <div>
        <span className={status.cls} data-testid={`status-${item.period_id}`}>{status.label}</span>
      </div>
      <div className="mono" style={{ fontSize: 13, textAlign: "right" }}>
        {variance > 0 ? (
          <span style={{ color: item.variance_explained ? "var(--accent)" : "var(--blocker)" }}>
            ${fmt.currency(variance)}
          </span>
        ) : <span className="muted">—</span>}
      </div>
      <div style={{ textAlign: "right" }}>
        <Link to={`/periods/${item.period_id}`}
              className={"btn primary"}
              data-testid={`open-period-${item.period_id}`}>
          {actionLabel}
        </Link>
      </div>
    </div>
  );
}

// ─── Locked period → Record Card (per Controller direction) ─────────────────
function RecordCard({ item }) {
  const verdict = item.head_cert_verdict;
  const basis = item.head_cert_basis;
  const ri = item.reconciling_item_count || 0;

  const basisLabel = basis
    ? basis.charAt(0).toUpperCase() + basis.slice(1).replace(/_/g, " ")
    : "—";

  return (
    <div
      className="record-card"
      data-testid={`record-${item.period_id}`}
      style={{
        border: "1px solid var(--line)",
        borderRadius: 6,
        background: "var(--surface)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div data-testid={`record-header-${item.period_id}`}
             style={{ fontSize: 15, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Lock size={14} aria-label="Locked" style={{ color: "var(--ink-mute)" }} />
          {item.period_label}
        </div>
        <span className="muted" style={{ fontSize: 11 }}>{item.account_name}</span>
      </div>

      <div>
        {verdict
          ? <VerdictBadge verdict={verdict} dataTestId={`verdict-${item.period_id}`} compact />
          : <UnattestedBadge dataTestId={`unattested-${item.period_id}`} />}
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        rowGap: 4,
        columnGap: 8,
        fontSize: 13,
      }} data-testid={`record-detail-${item.period_id}`}>
        <span className="muted">Certification Basis:</span>
        <span>{basisLabel}</span>
        <span className="muted">Bank Variance:</span>
        <span className="mono">${fmt.currency(item.bank_variance || 0)}</span>
        <span className="muted">GL Variance:</span>
        <span className="mono">${fmt.currency(item.gl_variance || 0)}</span>
        <span className="muted">Exceptions:</span>
        <span data-testid={`record-exceptions-${item.period_id}`}>{ri}</span>
      </div>

      <div style={{ marginTop: 4, textAlign: "right" }}>
        <Link
          to={`/periods/${item.period_id}`}
          className="btn"
          data-testid={`review-${item.period_id}`}
        >Review</Link>
      </div>
    </div>
  );
}
